/**
 * ========================================
 * AUTH SERVICE
 * ========================================
 * Handles all authentication API calls and local storage management
 * Professional error handling for user registration, login, and account management
 */

import axios from 'axios';
import type { User, LoginCredentials, RegisterData, AuthResponse } from '../../types';
import { APIError, handleAPIError, getErrorMessage, getValidationErrors } from '../api/error-handler';
import { ErrorCode, ERROR_MESSAGES } from '../../types/errors';
import { trialService } from '../trial';

/** Thrown by login() when the account is already signed in on another device (students only). */
export class SessionConflictError extends Error {
  constructor(public otherDevice: string | null, public lastSeenAt: string | null) {
    super('You are already signed in on another device.');
    this.name = 'SessionConflictError';
  }
}

/** True if an axios error carries the backend's SESSION_REVOKED code (in any of the response shapes it uses). */
export function isSessionRevoked(err: any): boolean {
  const d = err?.response?.data;
  return d?.code === 'SESSION_REVOKED' || d?.response?.code === 'SESSION_REVOKED' || d?.detail?.code === 'SESSION_REVOKED';
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || window.location.origin;
const API_VERSION = '/api/v1';
// Forgot-password calls fail fast instead of leaving the form spinning if the server is wedged.
const PASSWORD_RESET_TIMEOUT_MS = 20000;

/**
 * Storage Keys
 */
const STORAGE_KEYS = {
  USER: 'aibuddy_user',
  TOKEN: 'authToken', // Must match the key used by Axios interceptor in client.ts
  SESSION: 'aibuddy_session',
};

/**
 * Create Axios instance with base URL and error interceptor
 */
const authClient = axios.create({
  baseURL: `${API_BASE_URL}${API_VERSION}`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add error handling interceptor
authClient.interceptors.response.use(
  response => response,
  error => {
    // Use centralized error handler which throws APIError
    handleAPIError(error);
  }
);

/**
 * Auth Service Class
 */
class AuthService {
  /**
   * Register new user with comprehensive error handling
   * POST /users/create-user
   * 
   * Errors handled:
   * - Email already exists
   * - Username already taken
   * - Weak password
   * - Invalid input
   * - Server errors
   */
  async register(data: RegisterData): Promise<AuthResponse> {
    try {
      // Build payload with only filled-in optional fields
      const payload: any = {
        email: data.email,
        username: data.username,
        password: data.password,
        first_name: data.first_name,
        last_name: data.last_name,
      };

      // Only add optional fields if they have values
      if (data.phone_number && data.phone_number.trim()) {
        payload.phone_number = data.phone_number;
      }
      if (data.gender && data.gender.trim()) {
        payload.gender = data.gender;
      }
      if (data.date_of_birth && data.date_of_birth.trim()) {
        payload.date_of_birth = data.date_of_birth;
      }
      if (data.profile_image && data.profile_image.trim()) {
        payload.profile_image = data.profile_image;
      }
      if (data.address && data.address.trim()) {
        payload.address = data.address;
      }
      if (data.city && data.city.trim()) {
        payload.city = data.city;
      }
      if (data.state && data.state.trim()) {
        payload.state = data.state;
      }
      if (data.country && data.country.trim()) {
        payload.country = data.country;
      }
      if (data.pincode && data.pincode.trim()) {
        payload.pincode = data.pincode;
      }
      if (data.role_id) {
        payload.role_id = data.role_id;
      }

      const response = await authClient.post('/users/create-user', payload);

      // Registration only creates the account
      // Do NOT auto-login - user must sign in manually
      // This prevents showing the dashboard briefly after registration
      
      return {
        user: response.data || {},
        message: 'User registered successfully. Please sign in.',
      };
    } catch (error: any) {
      // If it's an APIError with proper error handling, rethrow it
      if (error instanceof APIError) {
        throw error;
      }
      
      // Fallback for unexpected errors
      throw new APIError(
        500,
        ErrorCode.UNKNOWN_ERROR,
        error.message || 'Registration failed',
        ERROR_MESSAGES[ErrorCode.UNKNOWN_ERROR]
      );
    }
  }

  /**
   * Login user with comprehensive error handling
   * Calls actual backend /api/Auth/connect-token endpoint
   * 
   * Errors handled:
   * - Invalid email or password
   * - User not found
   * - Account disabled
   * - Server errors
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      // Call actual backend login endpoint
      const response = await axios.post(`${API_BASE_URL}/api/Auth/connect-token`, {
        username: credentials.username,
        password: credentials.password,
        replaceExisting: credentials.replaceExisting ?? false,
      });

      // Extract user data and token - handle multiple possible response structures
      let userData = response.data.response?.user || response.data.user || response.data.data?.user;
      let token = response.data.response?.accessToken || response.data.accessToken || response.data.data?.accessToken || response.data.token;

      // If still not found, throw error
      if (!userData || !token) {
        throw new APIError(
          500,
          ErrorCode.UNKNOWN_ERROR,
          'Invalid login response from server',
          'Login failed. Please check your credentials.'
        );
      }

      // Extract additional user data from JWT token to fill in missing fields
      const tokenUserData = this.extractUserDataFromToken(token);
      console.log('[Auth] Extracted from JWT token:', tokenUserData);
      console.log('[Auth] Response user data:', userData);

      // Merge response data with token data (response data takes precedence)
      // This ensures we have all available user info
      const completeUserData = {
        ...tokenUserData,
        ...userData,
        permissions: this.extractPermissionsFromToken(token),
      };

      console.log('[Auth] Complete merged user data:', completeUserData);

      // Create user object with all available data
      const user: User = completeUserData as User;

      // Store user and token
      // Token from backend should already include "Bearer " prefix
      localStorage.setItem('authToken', token);
      this.setUser(user);
      this.setToken(token);

      return {
        user,
        token,
        message: 'Login successful',
      };
    } catch (error: any) {
      const conflict = error?.response?.status === 409 ? error.response.data?.response : null;
      if (conflict?.code === 'SESSION_ACTIVE_ELSEWHERE') {
        throw new SessionConflictError(conflict.otherDevice ?? null, conflict.lastSeenAt ?? null);
      }
      // If it's already an APIError, rethrow it
      if (error instanceof APIError) {
        throw error;
      }
      
      // Use centralized error handler to properly parse the error
      // This extracts the actual error message from the backend response
      try {
        handleAPIError(error);
      } catch (apiError: any) {
        // handleAPIError always throws, so we rethrow the parsed APIError
        throw apiError;
      }
    }
  }

  /**
   * Establish a signed-in session from a token an endpoint OTHER than /api/Auth/connect-token
   * already minted (e.g. school self-enrollment, which creates the account and logs the student
   * in as one step). Same storage/merge logic as the tail of login() above, without the
   * username/password round-trip.
   */
  async establishSession(token: string, userData: Partial<User>): Promise<AuthResponse> {
    const tokenUserData = this.extractUserDataFromToken(token);
    const user: User = {
      ...tokenUserData,
      ...userData,
      permissions: this.extractPermissionsFromToken(token),
    } as User;

    localStorage.setItem('authToken', token);
    this.setUser(user);
    this.setToken(token);

    return { user, token, message: 'Signed in' };
  }

  /**
   * Forgot password (no login required) -- three steps against /api/Auth:
   *   requestPasswordReset -> emails a 6-digit code (same answer whether or not the email has an account)
   *   verifyPasswordResetCode -> returns a short-lived reset token
   *   resetPassword -> sets the new password using that token
   * Errors are thrown as plain Error(message) with the backend's user-facing message.
   */
  private passwordResetError(error: any, fallback: string): Error {
    const data = error?.response?.data;
    if (error?.response?.status === 422) return new Error('Please check what you entered and try again.');
    if (error?.response?.status === 429) return new Error('Too many attempts. Please wait a minute and try again.');
    if (error?.code === 'ECONNABORTED') return new Error('The server took too long to respond. Please try again.');
    if (!error?.response) return new Error('Could not reach the server. Check your connection and try again.');
    return new Error(typeof data?.message === 'string' && data.message ? data.message : fallback);
  }

  async requestPasswordReset(email: string): Promise<void> {
    try {
      await axios.post(`${API_BASE_URL}/api/Auth/forgot-password`, { email: email.trim() }, { timeout: PASSWORD_RESET_TIMEOUT_MS });
    } catch (error: any) {
      throw this.passwordResetError(error, 'Could not send the code. Please try again.');
    }
  }

  async verifyPasswordResetCode(email: string, code: string): Promise<string> {
    let response;
    try {
      response = await axios.post(`${API_BASE_URL}/api/Auth/verify-reset-code`, { email: email.trim(), code }, { timeout: PASSWORD_RESET_TIMEOUT_MS });
    } catch (error: any) {
      throw this.passwordResetError(error, 'Could not verify the code.');
    }
    const token = response.data?.response?.resetToken;
    if (!token) throw new Error('Unexpected response from the server. Please try again.');
    return token;
  }

  async resetPassword(email: string, resetToken: string, newPassword: string): Promise<void> {
    try {
      await axios.post(`${API_BASE_URL}/api/Auth/reset-password`, {
        email: email.trim(),
        reset_token: resetToken,
        new_password: newPassword,
      }, { timeout: PASSWORD_RESET_TIMEOUT_MS });
    } catch (error: any) {
      throw this.passwordResetError(error, 'Could not reset the password. Please start again.');
    }
  }

  /**
   * Get all users (temporary for login validation)
   * GET /api/v1/users/get-all-users
   * Includes dummy data for trial testing
   */
  async getAllUsers(): Promise<User[]> {
    try {
      const response = await authClient.get('/api/v1/users/get-all-users');
      // API returns {status, message, data: Array}, so extract the data array
      let users = response.data.data || [];

      // Add dummy users for trial testing
      // These are for demonstration/testing purposes
      const now = new Date();
      const trialEnd14 = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
      const graceEnd = new Date(trialEnd14.getTime() + 3 * 24 * 60 * 60 * 1000);

      const trialEnd3 = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
      const graceEnd3 = new Date(trialEnd3.getTime() + 3 * 24 * 60 * 60 * 1000);

      const trialExpired = new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000);
      const graceExpired = new Date(trialExpired.getTime() + 3 * 24 * 60 * 60 * 1000);

      const dummyUsers: User[] = [
        {
          id: '1',
          email: 'trial@example.com',
          username: 'trialuser',
          first_name: 'Trial',
          last_name: 'User',
          subscription_plan: 'free',
          trial_started_at: now.toISOString(),
          trial_end_date: trialEnd14.toISOString(),
          grace_period_end_date: graceEnd.toISOString(),
          is_trial_active: true,
          is_grace_period_active: false,
          subscription_status: 'trial',
        },
        {
          id: '2',
          email: 'expiring@example.com',
          username: 'expiringuser',
          first_name: 'Expiring',
          last_name: 'User',
          subscription_plan: 'free',
          trial_started_at: new Date(now.getTime() - 11 * 24 * 60 * 60 * 1000).toISOString(),
          trial_end_date: trialEnd3.toISOString(),
          grace_period_end_date: graceEnd3.toISOString(),
          is_trial_active: true,
          is_grace_period_active: false,
          subscription_status: 'trial',
        },
        {
          id: '3',
          email: 'grace@example.com',
          username: 'graceuser',
          first_name: 'Grace',
          last_name: 'User',
          subscription_plan: 'free',
          trial_started_at: new Date(now.getTime() - 18 * 24 * 60 * 60 * 1000).toISOString(),
          trial_end_date: trialExpired.toISOString(),
          grace_period_end_date: graceExpired.toISOString(),
          is_trial_active: false,
          is_grace_period_active: true,
          subscription_status: 'grace_period',
        },
        {
          id: '4',
          email: 'expired@example.com',
          username: 'expireduser',
          first_name: 'Expired',
          last_name: 'User',
          subscription_plan: 'free',
          trial_started_at: new Date(now.getTime() - 25 * 24 * 60 * 60 * 1000).toISOString(),
          trial_end_date: new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000).toISOString(),
          grace_period_end_date: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
          is_trial_active: false,
          is_grace_period_active: false,
          subscription_status: 'expired',
        },
        {
          id: '5',
          email: 'premium@example.com',
          username: 'premiumuser',
          first_name: 'Premium',
          last_name: 'User',
          subscription_plan: 'premium',
          subscription_status: 'active',
        },
      ];

      // Combine real and dummy users, avoiding duplicates
      const existingEmails = new Set(users.map((u: User) => u.email));
      const uniqueDummyUsers = dummyUsers.filter(
        (du) => !existingEmails.has(du.email)
      );

      users = [...users, ...uniqueDummyUsers];
      return users;
    } catch (error: any) {
      console.error('Failed to fetch users:', error);
      
      if (error instanceof APIError) {
        throw error;
      }
      
      throw new APIError(
        500,
        ErrorCode.UNKNOWN_ERROR,
        error.message || 'Failed to fetch users',
        ERROR_MESSAGES[ErrorCode.UNKNOWN_ERROR]
      );
    }
  }

  /**
   * Get user by ID with error handling
   * GET /users/{user_id}
   */
  async getUserById(userId: string): Promise<User> {
    try {
      const response = await authClient.get(`/users/${userId}`);
      // Same {status, message, data} envelope as updateUser above -- unwrap it.
      return response.data?.data ?? response.data;
    } catch (error: any) {
      console.error('Failed to fetch user:', error);
      
      if (error instanceof APIError) {
        throw error;
      }
      
      throw new APIError(
        500,
        ErrorCode.UNKNOWN_ERROR,
        error.message || 'Failed to fetch user',
        ERROR_MESSAGES[ErrorCode.UNKNOWN_ERROR]
      );
    }
  }

  /**
   * Update user with error handling
   * PUT /users/{user_id}
   */
  async updateUser(userId: string, data: Partial<User>): Promise<User> {
    try {
      const response = await authClient.put(`/users/${userId}`, data);
      // PUT /users/{id} returns {status, message, data: UserResponseDTO} (see user_router.py) --
      // response.data is that WHOLE envelope, not the user. Using it as-is (bug fixed here) wrote
      // a malformed {status,message,data:{...}} object as "the current user" into both localStorage
      // and the caller's own state: every field read off it (id, first_name, ...) came back
      // undefined, which is what made the header fall back to a generic "User" and My Profile
      // report "User not authenticated" right after a save.
      const updatedUser: User = response.data?.data ?? response.data;
      this.setUser(updatedUser);
      return updatedUser;
    } catch (error: any) {
      console.error('Failed to update user:', error);
      
      if (error instanceof APIError) {
        throw error;
      }
      
      throw new APIError(
        500,
        ErrorCode.UNKNOWN_ERROR,
        error.message || 'Failed to update user',
        ERROR_MESSAGES[ErrorCode.UNKNOWN_ERROR]
      );
    }
  }

  /**
   * Delete user with error handling
   * DELETE /users/{user_id}
   */
  async deleteUser(userId: string): Promise<void> {
    try {
      await authClient.delete(`/users/${userId}`);
      this.logout();
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      
      if (error instanceof APIError) {
        throw error;
      }
      
      throw new APIError(
        500,
        ErrorCode.UNKNOWN_ERROR,
        error.message || 'Failed to delete user',
        ERROR_MESSAGES[ErrorCode.UNKNOWN_ERROR]
      );
    }
  }

  /**
   * Logout user - clears all stored data
   */
  logout(): void {
    // Free this device's single-device slot on the server (best effort -- never blocks or fails the local logout).
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (token) {
      axios.post(`${API_BASE_URL}/api/Auth/logout`, {}, {
        headers: { Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}` },
      }).catch(() => undefined);
    }
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.SESSION);
    // Signing out while "logged in as" someone signs out completely -- the parked admin session goes too.
    localStorage.removeItem('aibuddy_impersonation');
  }

  /**
   * Set user in local storage
   */
  private setUser(user: User): void {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
  }

  /**
   * Get user from local storage
   */
  getUser(): User | null {
    const user = localStorage.getItem(STORAGE_KEYS.USER);
    return user ? JSON.parse(user) : null;
  }

  /**
   * Set token in local storage
   */
  private setToken(token: string): void {
    localStorage.setItem(STORAGE_KEYS.TOKEN, token);
  }

  /**
   * Get token from local storage
   */
  getToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.TOKEN);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getUser() && !!this.getToken();
  }

  /**
   * Returns seconds until the stored JWT expires (negative if already expired).
   */
  getTokenExpiresInSeconds(): number {
    const token = this.getToken();
    if (!token) return -1;
    try {
      const jwt = token.startsWith('Bearer ') ? token.slice(7) : token;
      const payload = JSON.parse(atob(jwt.split('.')[1]));
      return Math.floor(payload.exp - Date.now() / 1000);
    } catch {
      return -1;
    }
  }

  /**
   * Calls POST /api/Auth/refresh-token with the current token.
   * Stores the new token on success. Throws on failure.
   */
  async refreshToken(): Promise<void> {
    const token = this.getToken();
    if (!token) throw new Error('No token to refresh');

    const response = await axios.post(
      `${API_BASE_URL}/api/Auth/refresh-token`,
      { token },
      { headers: { 'Content-Type': 'application/json' } },
    );

    const newToken: string | undefined =
      response.data?.response?.accessToken ||
      response.data?.accessToken;

    if (!newToken) throw new Error('Refresh response missing token');

    // Strip "Bearer " prefix before storing (setToken adds it on use)
    const clean = newToken.startsWith('Bearer ') ? newToken.slice(7) : newToken;
    localStorage.setItem(STORAGE_KEYS.TOKEN, clean);
  }

  /**
   * Generate session token (temporary)
   * In production, this would be a JWT from the backend
   */
  private generateSessionToken(user: User): string {
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2);
    return `${user.id || 'temp'}-${timestamp}-${randomString}`;
  }

  /**
   * Extract user data from JWT token payload
   * JWT token contains user profile information that might not be in the response
   */
  private extractUserDataFromToken(token: string): Partial<User> {
    try {
      // Remove Bearer prefix if present
      const jwt = token.startsWith('Bearer ') ? token.slice(7) : token;
      
      // Split JWT and decode payload
      const parts = jwt.split('.');
      if (parts.length !== 3) {
        console.warn('Invalid JWT format, unable to extract user data');
        return {};
      }

      // Decode the payload (second part)
      const payload = JSON.parse(atob(parts[1]));
      
      // Map token claims to User object fields
      const extractedUser: Partial<User> = {
        id: payload.sub || payload.id || payload.user_id || payload.userId,
        email: payload.email,
        username: payload.username || payload.user_name,
        first_name: payload.first_name || payload.firstName || payload.given_name,
        last_name: payload.last_name || payload.lastName || payload.family_name,
        phone_number: payload.phone_number || payload.phone || payload.phoneNumber,
        gender: payload.gender,
        date_of_birth: payload.date_of_birth || payload.dateOfBirth || payload.dob,
        address: payload.address,
        city: payload.city,
        state: payload.state || payload.province,
        country: payload.country,
        pincode: payload.pincode || payload.zip || payload.zipCode,
        roles: Array.isArray(payload.roles) ? payload.roles : (payload.roles ? [payload.roles] : []),
      };

      // Remove undefined values to avoid overwriting response data with empty values
      Object.keys(extractedUser).forEach(key => {
        if (extractedUser[key as keyof User] === undefined) {
          delete extractedUser[key as keyof User];
        }
      });

      console.log('[Auth] Extracted user data from token:', extractedUser);
      return extractedUser;
    } catch (error) {
      console.warn('Failed to extract user data from token:', error);
      return {};
    }
  }

  /**
   * Extract permissions from JWT token
   * JWT format: header.payload.signature
   * Payload contains user data including permissions array
   */
  private extractPermissionsFromToken(token: string): string[] {
    try {
      // Remove Bearer prefix if present
      const jwt = token.startsWith('Bearer ') ? token.slice(7) : token;
      
      // Split JWT and decode payload
      const parts = jwt.split('.');
      if (parts.length !== 3) {
        console.warn('Invalid JWT format, returning empty permissions');
        return [];
      }

      // Decode the payload (second part)
      const payload = JSON.parse(atob(parts[1]));
      
      // Extract permissions array from payload
      const permissions = payload.permissions || payload.scope || [];
      return Array.isArray(permissions) ? permissions : [];
    } catch (error) {
      console.warn('Failed to extract permissions from token:', error);
      return [];
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
